// ─────────────────────────────────────────────────────────────────────────────
// Campaign API client
//
// The external PAR Punchh® campaign API (see api_plan.md) can't be called from the
// browser, so all of that logic lives on the backend in `CampaignEditorController`
// / `CampaignDemoService`. This module is a thin client over those endpoints.
//
// The backend reads segments/promotions and composes the AI suggestion live; the
// remaining campaign reads/writes are still stubbed. Making those live is a
// backend-only change — this client stays the same.
// ─────────────────────────────────────────────────────────────────────────────

import apiUtil from "@/util/apiUtil";

const BASE_URL = "/api/CampaignEditor";

export type CampaignActionKey = "delete" | "edit_segment" | "edit_promo" | "create";

export interface CampaignDetails {
    campaignId: number;
    externalCampaignId: string;
    businessId: number;
    name: string;
    campaignType: string;
    category: string;
    redeemableUuid: string;
    redeemableName: string;
    segmentId: number;
    segmentName: string;
    segmentSize: number;
    startTime: string;
    frequency: string;
    timeZone: string;
    controlGroupSize: string;
    status: string;
    offersSent: number;
    redemptionRate: string;
}

export interface Segment {
    segmentId: number;
    name: string;
    size: number;
    description: string;
}

export interface Promotion {
    redeemableUuid: string;
    name: string;
    description: string;
}

/** Body for creating (POST) and updating (PUT) a campaign. */
export interface CampaignPayload {
    campaignId?: number;
    campaignType: string;
    name: string;
    category: string;
    redeemableUuid: string;
    segmentId: number;
    frequency: string;
    startTime: string;
    externalCampaignId?: string;
}

export interface CampaignMutationResult {
    success: boolean;
    campaignId: number;
    message: string;
    /**
     * The campaign as re-read from the external PAR Punchh® API right after the mutation, so the UI
     * can confirm the change actually landed. Absent for mutations that don't re-read (e.g. delete).
     */
    campaign?: CampaignDetails;
}

/** LLM-composed suggestion that pre-populates the edit or new campaign. */
export interface CampaignSuggestion {
    suggestedSegmentId?: number | null;
    suggestedRedeemableUuid?: string | null;
    suggestedName?: string | null;
    rationale: string;
}

/** A label/value pair the user configured in their action-plan draft. */
export interface CampaignSuggestionActionPlanField {
    label: string;
    value: string;
}

/**
 * Everything the backend LLM needs to compose a real, context-aware suggestion:
 * the insight it came from, the current campaign, and the user's configured plan.
 */
export interface CampaignSuggestionRequest {
    insightId: string;
    campaignAction: CampaignActionKey;
    campaignId?: number;
    insightTitle?: string;
    insightSummary?: string;
    impactSummary?: string;
    actionPlanFields?: CampaignSuggestionActionPlanField[];
    actionPlanSteps?: string[];
}

// ── Reads ────────────────────────────────────────────────────────────────────

export function getCampaignDetails(
    campaignId: number,
    signal?: AbortSignal
): Promise<CampaignDetails> {
    return apiUtil.fetchData<CampaignDetails>(
        `${BASE_URL}/campaign/${campaignId}`,
        signal
    );
}

export function getSegments(signal?: AbortSignal): Promise<Segment[]> {
    return apiUtil.fetchData<Segment[]>(`${BASE_URL}/segments`, signal);
}

export function getPromotions(signal?: AbortSignal): Promise<Promotion[]> {
    return apiUtil.fetchData<Promotion[]>(`${BASE_URL}/promotions`, signal);
}

/** Ask the backend LLM to compose a context-aware suggestion for this insight action. */
export function getCampaignSuggestion(
    request: CampaignSuggestionRequest,
    signal?: AbortSignal
): Promise<CampaignSuggestion> {
    return apiUtil.postData<CampaignSuggestion>(
        `${BASE_URL}/suggestion`,
        request,
        signal
    );
}

// ── Writes ───────────────────────────────────────────────────────────────────

export function updateCampaign(
    payload: CampaignPayload
): Promise<CampaignMutationResult> {
    return apiUtil.putData<CampaignMutationResult>(`${BASE_URL}/campaign`, payload);
}

export function createCampaign(
    payload: CampaignPayload
): Promise<CampaignMutationResult> {
    return apiUtil.postData<CampaignMutationResult>(`${BASE_URL}/campaign`, payload);
}

export function deleteCampaign(campaignId: number): Promise<CampaignMutationResult> {
    return apiUtil.deleteData<CampaignMutationResult>(
        `${BASE_URL}/campaign/${campaignId}`
    );
}
